import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { PutFormValuesEnvelope, FilesWidget, FormValuesEnvelope } from '@truvity/widget__forms';
import { CredentialsMeta } from '@truvity/widget__client';
import { AnyObject, identity } from '@truvity/js-core__utils';

import { EditWidgetErrors, WalletDid, EntityDid, CredentialDid, WidgetEditApi, WalletEditApi } from '../../types';
import { WalletForm } from '../WalletForm';
import {
    LinkedCredentialsPreviewRequests,
    LinkedCredentialsSuggestRequests,
    LinkedCredentialsWidget,
} from '../../forms/widgets/linkedCredentials';
import { EditableFileStorage } from '../../forms/widgets/apiFilesStorage';
import { getNetworkErrorsDescriptor, isNotFound, isUnprocessableEntity } from '../../utils/error';
import { editableTheme } from '../../forms/editableTheme';
import { useCredentialSchema } from '../../hooks/useCredentialSchema';

export type SlotProps = {
    draftIsValid: boolean;
    draftRevision: number;
    draftIsSaving: boolean;
    showErrors: () => void;
    meta?: CredentialsMeta;
};

export type WalletEditDraftProps = (LinkedCredentialsPreviewRequests & LinkedCredentialsSuggestRequests) & {
    /**
     * The DID of a credential that we want to display for editing.
     */
    draftId: CredentialDid;

    /**
     * The DID of the wallet that stores the credential that we want to display for editing.
     */
    walletDid: WalletDid;

    /**
     * The DID of the entity that owns the wallet where the credential we want to display for editing is stored.
     */
    entityDid: EntityDid;

    /**
     * An API client that provides support for the necessary widget service operations required to work with
     * the credential that we want to display for editing.
     */
    widgetApi: WidgetEditApi;

    /**
     * An API client that provides support for the necessary wallet service operations required to work with
     * the credential that we want to display for editing.
     */
    walletApi: WalletEditApi;

    /**
     * A callback that notifies when a widget is fully displayed and ready for interactions.
     */
    onDisplayed?: () => void;

    /**
     * A callback that reports widget errors and allows for their handling.
     * If an error occurs, the widget will display an empty form without fields.
     */
    onError?: (error: EditWidgetErrors) => void;

    /**
     * A render prop that displays content before all fields and inner controls.
     * The passed props include information about the state of the widget and credential metadata.
     */
    headerSlotRenderer?: (renderProps: SlotProps) => ReactNode;

    /**
     * A render prop that displays content after all fields and inner controls.
     * The passed props include information about the state of the widget and credential metadata.
     */
    footerSlotRenderer?: (renderProps: SlotProps) => ReactNode;
};

export const WalletEditDraft = ({
    draftId,
    walletDid,
    entityDid,
    headerSlotRenderer,
    footerSlotRenderer,
    onDisplayed,
    getVcPreview,
    suggestVc,
    onError,
    widgetApi,
    walletApi,
}: WalletEditDraftProps) => {
    const [formIsValid, setFormIsValid] = useState(false);
    const [meta, setMeta] = useState<CredentialsMeta>();

    const handleNetworkErrors = useCallback(
        (error: any): void => {
            if (!onError) return;

            if (isNotFound(error)) {
                onError({
                    code: 'ERRDRAFTNOTFOUND',
                    draftId,
                });
            } else if (isUnprocessableEntity(error)) {
                onError({
                    code: 'ERRCREDENTIALNOTSUPPORTED',
                    credentialId: draftId,
                });
            } else {
                onError(getNetworkErrorsDescriptor(error));
            }
        },
        [draftId, onError],
    );

    const getJsonSchema = useCallback(
        async (): Promise<AnyObject> => widgetApi.getCredentialSchema(draftId, walletDid, entityDid),
        [draftId, entityDid, walletDid, widgetApi],
    );

    const credentialSchema = useCredentialSchema({
        getJsonSchema,
        onError: handleNetworkErrors,
    });

    const { getForm, putForm } = useMemo(
        () => ({
            getForm: async (): Promise<FormValuesEnvelope> => {
                try {
                    const credential = await widgetApi.getCredential(draftId, walletDid, entityDid);

                    setMeta(credential.meta);

                    if (credentialSchema.state !== 'loaded') {
                        throw new Error(`The credential schema for this document is not available`);
                    }

                    return {
                        values: credentialSchema.jsonToJs(credential.claims),
                        revision: credential.revision,
                    };
                } catch (error) {
                    handleNetworkErrors(error);

                    setMeta(undefined);

                    return {
                        values: {},
                        revision: 0,
                    };
                }
            },
            putForm: async (payload: PutFormValuesEnvelope): Promise<void> => {
                try {
                    if (credentialSchema.state !== 'loaded') {
                        throw new Error(`The credential schema for this document is not available`);
                    }

                    await widgetApi.updateCredential(draftId, walletDid, entityDid, {
                        revision: payload.revision,
                        claims: credentialSchema.jsToJson(payload.values),
                    });

                    setFormIsValid(payload.isValid);
                } catch (error) {
                    handleNetworkErrors(error);
                }
            },
        }),
        [widgetApi, draftId, walletDid, entityDid, credentialSchema, handleNetworkErrors],
    );

    if (credentialSchema.state !== 'loaded') return null;

    return (
        <div>
            <WalletForm
                autoSave
                defs={credentialSchema.schema}
                convertToApiType={identity}
                theme={editableTheme}
                entityId={draftId}
                getForm={getForm}
                putForm={putForm}
                onInit={({ isValid }) => {
                    setFormIsValid(isValid);
                    onDisplayed?.();
                }}
                onRevisionMismatch={() => onError?.({ code: 'ERRDRAFTREVISIONMISSMATCH', draftId })}
                headerSlot={({ isSubmitting, revision, showErrors }) => (
                    <>
                        {headerSlotRenderer?.({
                            draftIsValid: formIsValid,
                            draftRevision: revision,
                            draftIsSaving: isSubmitting,
                            showErrors,
                            meta,
                        })}
                    </>
                )}
                footerSlot={({ isSubmitting, revision, showErrors }) => (
                    <>
                        {footerSlotRenderer?.({
                            draftIsValid: formIsValid,
                            draftRevision: revision,
                            draftIsSaving: isSubmitting,
                            showErrors,
                            meta,
                        })}
                    </>
                )}
                widgets={[
                    new FilesWidget(
                        new EditableFileStorage({
                            fileApi: walletApi,
                            entityDid,
                            walletDid,
                            draftId,
                        }),
                    ),
                    new LinkedCredentialsWidget({
                        walletDid,
                        entityDid,
                        walletApi,
                        widgetApi,
                        getVcPreview,
                        suggestVc,
                        onError,
                    }),
                ]}
            />
        </div>
    );
};
